import React, { useCallback, useEffect, useState } from 'react';
import UserCombobox from '../../../util_components/user_combobox/UserCombobox';
import {
  add_sales_member,
  api_error_reason,
  api_status,
  fetch_sales_members,
  remove_sales_member
} from './api';
import type { RoleMembers } from './api';
import { can_remove, display_name, filter_candidates, merge_rows, row_user } from './sales_users';
import type { PanelRow } from './sales_users';
import { fetch_roster } from '../../sales_team/api';
import type { TeamMember, TeamRoster } from '../../sales_team/api';
import {
  format_next_leave,
  format_shift,
  format_weekly_off,
  status_badges
} from '../../sales_team/roster';
import type { BadgeTone } from '../../sales_team/roster';
import EditRepModal from '../../sales_team/EditRepModal';
import HasActiveStudentsNotice from '../../sales_team/HasActiveStudentsNotice';
import { SHARED_STYLES } from '../styles';
import StackTd from '../StackTable';
import { stackClasses } from '../stack_table';
import { blocked_by_students } from '../../sales_team/cms_user_fields';
import type { BlockedByStudents } from '../../sales_team/cms_user_fields';

export type Notify = (type: 'success' | 'error', message: string) => void;

interface Props {
  notify: Notify;
  self_uuid?: string;
}

const { MUTED: MUTED_STYLE, PRIMARY_BTN, SECONDARY_BTN } = SHARED_STYLES;

const TONE_CLASS: Record<BadgeTone, string> = {
  success: 'bg-state-success-bg text-state-success-text',
  warning: 'bg-state-warning-bg text-state-warning-text',
  muted: 'bg-surface-sunken text-content-muted'
};

// Weekly off sits under Shift and On shift under Status, so the table fits 1024px with the sidebar.
const COLUMNS = [
  { label: 'Name', width: 'w-[24%]' },
  { label: 'Level', width: 'w-[9%]' },
  { label: 'Shift / Weekly off', width: 'w-[20%]' },
  { label: 'Next leave', width: 'w-[16%]' },
  { label: 'Status', width: '' }
];
const CELL = 'px-3 py-3 text-left align-top';
const ST = stackClasses();

interface LoadError {
  forbidden: boolean;
  reason: string;
}

function SalesUsersPanel({ notify, self_uuid = '' }: Props) {
  const [roster, set_roster] = useState<TeamRoster | null>(null);
  const [roster_error, set_roster_error] = useState<LoadError | null>(null);
  const [roles, set_roles] = useState<RoleMembers | null>(null);
  // 403 means the caller is not a SUPER_USER: role management is hidden, not an error.
  const [roles_forbidden, set_roles_forbidden] = useState(false);
  const [roles_error, set_roles_error] = useState<string | null>(null);
  const [loading, set_loading] = useState(true);
  const [selected, set_selected] = useState('');
  const [busy, set_busy] = useState<string | null>(null);
  const [editing, set_editing] = useState<TeamMember | null>(null);
  const [blocked, set_blocked] = useState<BlockedByStudents | null>(null);

  const load_roster = useCallback(
    () =>
      fetch_roster()
        .then((r) => {
          set_roster(r);
          set_roster_error(null);
        })
        .catch((e: unknown) => {
          const forbidden = api_status(e) === 403;
          const reason = forbidden
            ? 'Super user only'
            : api_error_reason(e, 'Failed to load sales users');
          set_roster_error({ forbidden, reason });
          if (!forbidden) notify('error', reason);
        }),
    [notify]
  );

  const load_roles = useCallback(
    () =>
      fetch_sales_members()
        .then((d) => {
          set_roles(d);
          set_roles_forbidden(false);
          set_roles_error(null);
        })
        .catch((e: unknown) => {
          if (api_status(e) === 403) {
            set_roles(null);
            set_roles_forbidden(true);
            return;
          }
          const reason = api_error_reason(e, 'Failed to load SALES role members');
          set_roles_error(reason);
          notify('error', reason);
        }),
    [notify]
  );

  const load = useCallback(() => {
    // A refusal notice is only valid for the roster state it was raised on.
    set_blocked(null);
    set_loading(true);
    return Promise.all([load_roster(), load_roles()]).finally(() => set_loading(false));
  }, [load_roster, load_roles]);

  useEffect(() => {
    load();
  }, [load]);

  const can_edit = !!roster?.can_edit;
  const can_manage_roles = !!roles;
  const rows = merge_rows(roster?.members, roles?.members);
  const options = filter_candidates(roles?.candidates, '', roles?.members);
  const chosen = options.find((c) => c.uuid === selected) || null;
  const show_actions = can_edit || can_manage_roles;
  const col_count = COLUMNS.length + (show_actions ? 1 : 0);

  const on_add = () => {
    if (!chosen || busy) return;
    set_busy(chosen.uuid);
    add_sales_member(chosen.uuid)
      .then(() => {
        notify('success', `Added ${display_name(chosen)} to SALES`);
        set_selected('');
        return load();
      })
      .catch((e: unknown) => notify('error', api_error_reason(e, 'Failed to add user')))
      .finally(() => set_busy(null));
  };

  const on_remove = (row: PanelRow) => {
    const u = row_user(row);
    if (busy || !can_remove(u, self_uuid).ok) return;
    // eslint-disable-next-line no-alert
    if (!window.confirm(`Remove the SALES role from ${display_name(u)}?`)) return;
    set_busy(u.uuid);
    set_blocked(null);
    remove_sales_member(u.uuid)
      .then(() => {
        notify('success', `Removed ${display_name(u)} from SALES`);
        return load();
      })
      .catch((e: unknown) => {
        const refused = blocked_by_students(e, u.uuid);
        if (refused) set_blocked(refused);
        else notify('error', api_error_reason(e, 'Failed to remove user'));
      })
      .finally(() => set_busy(null));
  };

  const on_close = (changed: boolean) => {
    set_editing(null);
    if (changed) load();
  };

  const render_remove = (row: PanelRow) => {
    const name = display_name(row_user(row));
    const check = can_remove(row_user(row), self_uuid);
    return (
      <>
        <span title={check.hint}>
          <button
            type="button"
            onClick={() => on_remove(row)}
            disabled={!check.ok || !!busy}
            aria-label={`Remove ${name}`}
            className="rounded-md px-3 py-1.5 text-xs font-medium disabled:cursor-not-allowed disabled:opacity-50"
            style={SECONDARY_BTN}
          >
            {busy === row.uuid ? 'Removing…' : 'Remove'}
          </button>
        </span>
        {check.hint ? (
          <div className="mt-1 text-xs" style={MUTED_STYLE}>
            {check.hint}
          </div>
        ) : null}
      </>
    );
  };

  const render_row = (row: PanelRow) => {
    const m = row.member;
    const name = display_name(row_user(row));
    return (
      <tr
        key={row.uuid}
        className={`border-t border-line ${ST.row}`}
        style={m?.status === 'LEFT' ? { opacity: 0.6 } : undefined}
      >
        <StackTd className={CELL}>
          <span className="font-medium break-words">{name}</span>
          {row.is_active ? null : (
            <span className="ml-2 inline-block rounded-full bg-surface-sunken px-2 py-0.5 text-xs font-medium text-content-muted">
              Inactive
            </span>
          )}
          {row.email && row.email !== name ? (
            <div className="truncate text-xs" style={MUTED_STYLE} title={row.email}>
              {row.email}
            </div>
          ) : null}
        </StackTd>
        <StackTd label="Level" className={CELL}>
          {m?.level || <span style={MUTED_STYLE}>Not set</span>}
        </StackTd>
        <StackTd label="Shift / Weekly off" className={CELL}>
          <div>{format_shift(m?.shift_start, m?.shift_end)}</div>
          <div className="text-xs" style={MUTED_STYLE}>
            <span>Off: </span>
            <span>{m ? format_weekly_off(m.weekly_off) : '—'}</span>
          </div>
        </StackTd>
        <StackTd label="Next leave" className={CELL}>
          {format_next_leave(m?.next_leave)}
        </StackTd>
        <StackTd label="Status" className={CELL}>
          <div className="flex flex-wrap gap-1">
            {m
              ? status_badges(m).map((b) => (
                  <span
                    key={b.key}
                    className={`inline-block rounded-full px-2 py-0.5 text-xs font-medium ${TONE_CLASS[b.tone]}`}
                  >
                    {b.label}
                  </span>
                ))
              : null}
            {m?.on_shift_now ? (
              <span className="inline-block rounded-full bg-state-success-bg px-2 py-0.5 text-xs font-medium text-state-success-text">
                On shift
              </span>
            ) : null}
          </div>
        </StackTd>
        {show_actions ? (
          <StackTd className={CELL}>
            <div className="flex flex-wrap items-start gap-2">
              {can_edit && m ? (
                <button
                  type="button"
                  onClick={() => set_editing(m)}
                  aria-label={`Edit ${name}`}
                  className="rounded-md px-3 py-1.5 text-xs font-medium"
                  style={PRIMARY_BTN}
                >
                  Edit
                </button>
              ) : null}
              {can_manage_roles ? <div>{render_remove(row)}</div> : null}
            </div>
          </StackTd>
        ) : null}
      </tr>
    );
  };

  const render_body = () => {
    if (roster_error && !roster) {
      return (
        <tr className={ST.row}>
          <StackTd colSpan={col_count} className="px-4 py-8 text-center">
            <div role="alert" style={{ color: 'var(--state-error)' }}>
              {roster_error.forbidden
                ? 'Super user only'
                : `Could not load sales users: ${roster_error.reason}`}
            </div>
            {roster_error.forbidden ? null : (
              <button
                type="button"
                onClick={load}
                disabled={loading}
                className="mt-3 rounded-md px-3 py-1.5 text-xs font-medium"
                style={PRIMARY_BTN}
              >
                {loading ? 'Retrying…' : 'Retry'}
              </button>
            )}
          </StackTd>
        </tr>
      );
    }
    if (loading && !roster) {
      return (
        <tr className={ST.row}>
          <StackTd colSpan={col_count} className="px-4 py-8 text-center" style={MUTED_STYLE}>
            Loading sales users…
          </StackTd>
        </tr>
      );
    }
    if (rows.length === 0) {
      return (
        <tr className={ST.row}>
          <StackTd colSpan={col_count} className="px-4 py-8 text-center" style={MUTED_STYLE}>
            No users have the SALES role yet.
          </StackTd>
        </tr>
      );
    }
    return rows.map(render_row);
  };

  const render_add_row = () => {
    if (roles) {
      return (
        <div className="px-5 py-3 flex flex-wrap items-center gap-3 border-b border-line">
          <UserCombobox
            id="sales-users-candidate"
            label="User to add"
            users={options}
            value={selected}
            on_change={set_selected}
            disabled={!!busy}
          />
          <button
            type="button"
            onClick={on_add}
            disabled={!chosen || !!busy}
            className="rounded-md px-4 py-1.5 text-sm font-medium disabled:cursor-not-allowed disabled:opacity-50"
            style={PRIMARY_BTN}
          >
            {busy && chosen && busy === chosen.uuid ? 'Adding…' : 'Add user'}
          </button>
        </div>
      );
    }
    if (roles_error && !roles_forbidden) {
      return (
        <div className="px-5 py-3 flex flex-wrap items-center gap-3 border-b border-line">
          <span role="alert" className="text-sm" style={{ color: 'var(--state-error)' }}>
            Could not load SALES role members: {roles_error}
          </span>
          <button
            type="button"
            onClick={load}
            disabled={loading}
            className="rounded-md px-3 py-1.5 text-xs font-medium"
            style={PRIMARY_BTN}
          >
            {loading ? 'Retrying…' : 'Retry'}
          </button>
        </div>
      );
    }
    return null;
  };

  return (
    <section
      className="bg-surface-card rounded-lg shadow-sm border border-line"
      style={{ margin: '20px 5%' }}
    >
      <div className="px-5 py-3 border-b border-line flex flex-wrap items-start justify-between gap-2">
        <div>
          <h2 className="font-semibold">Sales users</h2>
          <p className="mt-1 text-xs" style={{ ...MUTED_STYLE, margin: 0 }}>
            Users with the SALES role get sales task pop-ups and can claim tasks.
            {roles ? ' Adding here is the same as adding the SALES role on CMS Users.' : ''}{' '}
            {can_edit
              ? "Use Edit to set a rep's level, shift, weekly off, status and leaves."
              : 'Levels, shifts and leaves are set here by sales managers.'}
          </p>
        </div>
        <span className="text-xs" style={MUTED_STYLE}>
          Shifts in IST
        </span>
      </div>
      {render_add_row()}
      {blocked ? (
        <div className="px-5">
          <HasActiveStudentsNotice blocked={blocked} />
        </div>
      ) : null}
      <table className={ST.table}>
        <thead className={`bg-surface-sunken text-content-secondary ${ST.head}`}>
          <tr>
            {COLUMNS.map((c) => (
              <th key={c.label} className={`px-3 py-2 text-left font-medium ${c.width}`}>
                {c.label}
              </th>
            ))}
            {show_actions ? (
              <th className="w-[150px] px-3 py-2 text-left font-medium">Actions</th>
            ) : null}
          </tr>
        </thead>
        <tbody className={ST.body}>{render_body()}</tbody>
      </table>
      {editing ? <EditRepModal member={editing} notify={notify} onClose={on_close} /> : null}
    </section>
  );
}

export default SalesUsersPanel;
